import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    inject,
    Injector,
    input,
    output,
    effect,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentType } from '@angular/cdk/overlay';
import { forkJoin, Subject, takeUntil, tap } from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { NgDestroyed } from '../../utils';
import { ActionContainerHeaderComponent } from '../floori-action-container-header';
import { DragContainerHandleDirective } from '../../directives';
import { TranslationsProvider } from '../../modules';
import { FLOORI_TRANSLATOR } from '../../constants';
import {
    DynamicHeaderData,
    ActionViewMap,
    FieldTranslation,
    ProductsViewDynamicProvider,
    PRODUCTS_VIEW_DYNAMIC_TOKEN,
    ActionContainerButtonData,
} from '../../models';

@Component({
    selector: 'floori-action-container',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        ActionContainerHeaderComponent,
        LetDirective,
        DragContainerHandleDirective,
    ],
    templateUrl: './action-container.component.html',
    styleUrls: ['./action-container.component.scss']
})
export class ActionContainerComponent extends NgDestroyed {
    private readonly elRef = inject(ElementRef);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly translationsProvider = inject<TranslationsProvider>(FLOORI_TRANSLATOR);
    private readonly injector = inject(Injector);
    private readonly componentTypeSubject = new Subject<ComponentType<unknown> | null>();
    private readonly actionButtonTypeSubject = new Subject<ComponentType<unknown> | null>();
    private readonly headerDataSubject = new Subject<DynamicHeaderData>();
    readonly componentType$ = this.componentTypeSubject.asObservable();
    readonly actionButtonType$ = this.actionButtonTypeSubject.asObservable();
    private dynamicHeaderIcon: string = '';
    private dynamicHeaderLabel: FieldTranslation | string = '';
    private dynamicHeaderMessage: FieldTranslation | string = '';

    buttonInjector?: Injector;
    componentInjector?: Injector;

    @HostBinding('class.floori-action-container') hostClass = true;

    headerType = input.required<string>();
    actionViewMap = input.required<ActionViewMap>();
    actionButtonViewMap = input.required<ActionViewMap>();
    isDialog = input<boolean>(false);
    isEpoxy = input<boolean>(false);
    allowDrag = input<boolean>(false);
    showCloseButton = input<boolean>(false);
    primaryButton = input<ActionContainerButtonData>();
    secondaryButton = input<ActionContainerButtonData>();
    tertiaryButton = input<ActionContainerButtonData>();
    showSelector = input<boolean>(false);
    selectorValue = input<string>('');
    disableHeaderLabelTranslation = input<boolean | undefined>(false);
    _headerIcon = input<string>('');
    _headerLabel = input<FieldTranslation | string>('');
    _headerMessage = input<FieldTranslation | string>('');

    constructor() {
        super();

        effect(() => {
            const val = this.headerType();

            if (!val) {
                return;
            }

            this.handleDynamicHeaderData();

            if (!this.componentIsVisible) {
                return;
            }

            forkJoin([
                this.actionViewMap()[val](this.isEpoxy()),
                this.actionButtonViewMap()[val](this.isEpoxy()),
            ])
                .pipe(
                    takeUntil(this.destroyed),
                    tap(([view, button]) => {
                        const actionSubject = new Subject<void>();
                        this.prepareDynamicProviderForButton(true, button, actionSubject);
                        this.prepareDynamicProviderForButton(false, view, actionSubject);
                        this.componentTypeSubject.next(view);
                        this.actionButtonTypeSubject.next(button);
                        this.cdr.markForCheck();
                    }),
                )
                .subscribe();
        });
    }

    readonly closeContainer = output<void>();
    readonly primaryButtonClick = output<void>();
    readonly secondaryButtonClick = output<void>();
    readonly tertiaryButtonClick = output<void>();
    readonly selectorClick = output<void>();

    get headerIcon(): string {
        if (!!this.dynamicHeaderIcon) {
            return this.dynamicHeaderIcon;
        }
        return this._headerIcon() ? this._headerIcon() : '';
    }

    get headerLabel(): string {
        if (this.disableHeaderLabelTranslation() && typeof this._headerLabel() === 'string') {
            return this._headerLabel() as string;
        }

        if (!!this.dynamicHeaderLabel) {
            return `${this.translationsProvider.translate(
                this._headerLabel(),
            )} - ${this.translationsProvider.translate(this.dynamicHeaderLabel)}`;
        }
        return this._headerLabel() ? this.translationsProvider.translate(this._headerLabel()) : '';
    }

    get headerMessage(): string {
        if (!!this.dynamicHeaderMessage) {
            return `${this.translationsProvider.translate(
                this._headerMessage(),
            )} - ${this.translationsProvider.translate(this.dynamicHeaderMessage)}`;
        }
        return this._headerMessage()
            ? this.translationsProvider.translate(this._headerMessage())
            : '';
    }

    get componentIsVisible(): boolean {
        return !!this.elRef.nativeElement?.offsetParent;
    }

    closePanel(): void {
        this.closeContainer.emit();
    }

    primaryButtonClickHandler(): void {
        this.primaryButtonClick.emit();
    }

    secondaryButtonClickHandler(): void {
        this.secondaryButtonClick.emit();
    }

    tertiaryButtonClickHandler(): void {
        this.tertiaryButtonClick.emit();
    }

    selectorClickHandler(): void {
        this.selectorClick.emit();
    }

    private prepareDynamicProviderForButton(
        isSender: boolean,
        componentType: ComponentType<unknown> | null,
        actionSubject: Subject<void>,
    ): void {
        this[isSender ? 'buttonInjector' : 'componentInjector'] = componentType
            ? Injector.create({
                  providers: [
                      {
                          provide: PRODUCTS_VIEW_DYNAMIC_TOKEN,
                          useValue: {
                              headerData: this.headerDataSubject,
                              actionBtnCallback: isSender
                                  ? (): void => actionSubject.next()
                                  : undefined,
                              actionBtnCallback$: isSender
                                  ? undefined
                                  : actionSubject.asObservable(),
                          } as ProductsViewDynamicProvider,
                      },
                  ],
                  parent: this.injector,
              })
            : undefined;
    }

    private handleDynamicHeaderData(): void {
        this.headerDataSubject
            .asObservable()
            .pipe(
                takeUntil(this.destroyed),
                tap((data: DynamicHeaderData) => {
                    this.dynamicHeaderIcon = data?.headerIcon || '';
                    this.dynamicHeaderLabel = data?.headerLabel || '';
                    this.dynamicHeaderMessage = data?.headerDynamicMessage || '';
                    this.cdr.detectChanges();
                }),
            )
            .subscribe();
    }
}
